/**
 * 《사흘》 v7 · **마을 바깥에 서 있는 것들.**
 *
 * 지금까지 이 레포의 조각은 전부 실내 물건이었다. 밖은 지형과 건물 껍데기뿐이라,
 * 컷을 찍으면 「집이 여섯 채 서 있는 눈밭」으로 보였다 — 사람이 사는 흔적이 없다.
 *
 * 마을을 마을로 만드는 것은 집이 아니라 **집 옆에 쌓아 둔 것들**이다. 장독대,
 * 처마 밑 장작더미, 마당에 세워 둔 리어카, 그리고 길을 따라 선 전주. 이 넷이
 * 들어가면 같은 집도 「사는 집」이 된다.
 *
 * ## 밖에 있는 물건은 눈을 쓴다
 *
 * 실내 조각과 다른 점은 이것 하나다. 위를 향한 면에는 눈이 얹혀 있어야 하고,
 * 그 눈은 **지형·둑과 같은 필드에서 나와야 한다** — 안 그러면 물건만 다른 계절에서
 * 온 것처럼 보인다. `outside.ts` 의 `applySnowAccum` 을 슬롯 재질에 그대로 물린다.
 * 재질은 한 벌만 만들어 돌려 쓴다(조각마다 만들면 전주 일곱 개가 셰이더를 일곱 벌
 * 컴파일한다).
 *
 * 치수는 실물이다. 콘크리트 전주는 길이 10m 에 지상 8.4m, 밑지름 0.30m, 끝지름
 * 0.19m 로 테이퍼가 있다. 리어카 바퀴는 지름 0.62m 다. 눈대중으로 넣으면 마을에
 * 놨을 때 사람 키와 어긋나서 바로 보인다.
 */

import * as THREE from 'three';
import { bevelBox, lathe, type MeshWriter, type Slot } from './kernel';
import { slotMaterials } from './slots';
import { applySnowAccum } from '../outside';
import type { Piece } from './pieces';

/* ------------------------------------------------------------------ */
/* 눈 얹은 재질                                                        */
/* ------------------------------------------------------------------ */

let SNOWED: Map<Slot, THREE.Material> | null = null;

/**
 * 슬롯 재질에 눈을 얹은 사본. **조각·인스턴스와 무관하게 한 벌만 만든다.**
 *
 * `buildPiece` 는 조각을 놓을 때마다 `materials()` 를 부른다. 여기서 clone 하면
 * 전주 일곱 개 · 장독대 넷이 저마다 재질을 갖고, three 는 그때마다 프로그램을
 * 찾는다. 눈 셰이더는 `customProgramCacheKey` 로 프로그램을 공유하지만 재질 객체와
 * 유니폼 뭉치는 그대로 늘어난다 — 만들 이유가 없다.
 *
 * 셀 크기는 지형(0.09)보다 **훨씬 잘아야 한다.** 장독 뚜껑은 지름 0.44m 라서
 * 지형 눈금으로는 뚜껑 하나가 통째로 한 셀 안에 들어가 얼룩이 안 생긴다.
 */
function snowedSlots(renderer: THREE.WebGLRenderer, want: Slot[]) {
  if (!SNOWED) {
    SNOWED = new Map();
    const base = slotMaterials(renderer);
    const all: Slot[] = ['concrete', 'steel', 'earthen', 'oak', 'walnut', 'iron', 'rubber', 'porcelain'];
    for (const s of all) {
      const m = base.get(s) as THREE.MeshStandardMaterial | undefined;
      if (!m) continue;
      const c = m.clone();
      // flat 0.5: 45도보다 누운 면에만 얹힌다. 벽처럼 선 면에 눈이 붙으면 즉시 가짜다
      applySnowAccum(c, { flat: 0.5, coverage: 0.82, edge: 0.18, scale: 3.0, bump: 0.45 });
      SNOWED.set(s, c);
    }
  }
  const out: Partial<Record<string, THREE.Material>> = {};
  for (const s of want) {
    const m = SNOWED.get(s);
    if (m) out[s] = m;
  }
  return out;
}

/** 결정적 난수 — 같은 장작더미는 언제 지어도 같은 모양이어야 한다 */
function rnd(i: number) {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/** 닫힌 원기둥 하나. `CylinderGeometry` 는 기본이 막힌 통이라 그대로 쓴다 */
function cyl(w: MeshWriter, slot: Slot, r: number, h: number, seg = 12, tile = 0.4) {
  w.add(new THREE.CylinderGeometry(r, r, h, seg), slot, tile);
}

/* ------------------------------------------------------------------ */

export const SAHOL_VILLAGE_PIECES: Piece[] = [
  /* ================================================================ */
  {
    id: 'power_pole',
    nofront: true,
    name: '콘크리트 전주',
    collection: 'sahol',
    category: '마을',
    note:
      '길을 따라 서는 주상변압기 달린 전주. 이 마을에서 **가장 키가 큰 물건**이고, ' +
      '밤 컷에서 하늘을 가르는 실루엣이 마을과 벌판을 가른다. 콘크리트라 위로 갈수록 ' +
      '가늘어지고(밑 0.30 → 끝 0.19), 완금은 강철 앵글 두 단, 애자는 흰 자기다. ' +
      '발판볼트가 2.4m 위부터 좌우로 엇갈려 박혀 있어야 「올라갈 수 있는 기둥」으로 읽힌다.',
    dims: {
      /** 지상 높이. 10m 짜리를 1.6m 묻는다 */
      h: 8.4,
      rBase: 0.15,
      rTop: 0.095,
      /** 완금 길이 */
      arm: 1.5,
      /** 변압기 지름 */
      tank: 0.46,
      tankH: 0.7,
    },
    build(w, d) {
      // --- 기둥 ---
      // 테이퍼는 CylinderGeometry 가 위·아래 반지름을 따로 받아 그대로 만든다.
      // 닫힌 통이라 투과 검사에 안 걸린다.
      w.part('전주');
      w.at(0, d.h / 2, 0, () => {
        w.add(new THREE.CylinderGeometry(d.rTop, d.rBase, d.h, 14), 'concrete', 0.6);
      });

      // --- 완금 두 단 ---
      // 위 단이 고압, 아래 단이 저압이다. 아래를 짧게 해야 위에서 볼 때 T 가 아니라
      // 전주로 보인다 — 같은 길이로 두면 십자가 두 개가 겹친 모양이 된다.
      const armY = [d.h - 0.42, d.h - 1.15];
      const armL = [d.arm, d.arm * 0.72];
      for (let a = 0; a < 2; a++) {
        w.part(`완금${a + 1}`);
        w.at(0, armY[a], 0, () => {
          w.add(bevelBox(armL[a], 0.075, 0.075), 'steel', 0.3);
          // 앵글을 기둥에 무는 밴드
          w.add(bevelBox(0.34, 0.06, d.rBase * 2 + 0.09), 'steel', 0.3);
        });
        // 애자 셋 — 완금 양 끝과 가운데
        for (const s of [-1, 0, 1]) {
          if (a === 1 && s === 0) continue; // 저압 단은 둘만
          w.part('애자');
          w.at(s * (armL[a] / 2 - 0.14), armY[a] + 0.037, 0, () => {
            // 핀애자: 갓이 두 겹이라 옆에서 보면 계단이다. 매끈한 원뿔로 만들면
            // 밤 컷에서 그냥 흰 점이 되고, 낮 컷에서는 나사못처럼 보인다
            w.add(
              lathe(
                [
                  [0.001, 0], [0.028, 0], [0.028, 0.045], [0.062, 0.052],
                  [0.055, 0.072], [0.030, 0.078], [0.048, 0.088], [0.042, 0.108],
                  [0.020, 0.112], [0.001, 0.112],
                ],
                12,
              ),
              'porcelain',
              0.2,
            );
          });
        }
      }

      // --- 주상변압기 ---
      // 기둥에 브래킷 두 개로 물린다. 브래킷이 없으면 통이 공중에 떠 보이고
      // joint-check 가 떨어진 덩이로 잡는다.
      const tankY = d.h - 2.35;
      const off = d.rBase + d.tank / 2 + 0.03;
      w.part('변압기');
      w.at(0, tankY, off, () => {
        w.add(new THREE.CylinderGeometry(d.tank / 2, d.tank / 2, d.tankH, 14), 'steel', 0.4);
        // 방열 주름 — 통 옆구리를 감는 얇은 링 셋. 밋밋한 원통은 물탱크로 보인다
        for (const ry of [-0.18, 0, 0.18]) {
          w.at(0, ry, 0, () => {
            w.add(new THREE.TorusGeometry(d.tank / 2 + 0.012, 0.014, 6, 14), 'steel', 0.2);
          }, 0, Math.PI / 2);
        }
      });
      // 부싱 둘 (뚜껑 위 자기 애자)
      for (const s of [-1, 1]) {
        w.part('부싱');
        w.at(s * 0.1, tankY + d.tankH / 2 + 0.055, off, () => {
          w.add(lathe([[0.001, 0], [0.036, 0], [0.030, 0.05], [0.042, 0.058], [0.026, 0.085], [0.001, 0.085]], 10), 'porcelain', 0.2);
        });
      }
      for (const s of [-1, 1]) {
        w.part('브래킷');
        w.at(0, tankY + s * (d.tankH / 2 - 0.08), (d.rBase + off - d.tank / 2) / 2 + 0.02, () => {
          w.add(bevelBox(0.07, 0.05, off - d.tank / 2 + d.rBase + 0.02), 'iron', 0.2);
        });
      }

      // --- 발판볼트 ---
      // 좌우 엇갈려 박는다. 한쪽에만 박으면 사다리가 아니라 가시로 보인다.
      let n = 0;
      for (let y = 2.4; y < d.h - 1.5; y += 0.45, n++) {
        w.part('발판볼트');
        w.at((n % 2 ? 1 : -1) * (d.rBase + 0.075), y, 0, () => {
          cyl(w, 'iron', 0.011, 0.2, 6, 0.15);
        }, 0, 0, Math.PI / 2);
      }

      // --- 전주번호찰 ---
      // 눈높이에 붙는 작은 알루미늄 패. 이게 있으면 「관리되는 설비」가 된다
      w.part('번호찰');
      w.at(0, 1.7, -d.rBase - 0.004, () => {
        w.add(bevelBox(0.09, 0.14, 0.008, 0.002), 'alu', 0.1);
      });
    },
    materials: (r) => snowedSlots(r, ['concrete', 'steel', 'iron']),
  },

  /* ================================================================ */
  {
    id: 'jangdok_stand',
    nofront: true,
    name: '장독대',
    collection: 'sahol',
    category: '마을',
    note:
      '집 옆 볕 드는 자리에 시멘트 축대를 치고 독을 올려 둔 것. 독은 옹기라 검붉고 ' +
      '유약이 얼룩덜룩하다. **뚜껑 위에만 눈이 소복하게 얹혀 있어야 한다** — 겨울 마당에서 ' +
      '이 흰 동그라미 넷이 제일 먼저 눈에 든다. 크기가 다 같으면 공장 물건으로 보이므로 ' +
      '큰 독 둘 · 중간 둘로 섞는다.',
    dims: { w: 1.9, d: 1.05, h: 0.3 },
    openable: [
      // 독 넷의 뚜껑이 각각 들린다 — 안에 장(또는 단서)이 들어 있다
      { part: '독 뚜껑 1', slide: [0, 0.22, 0], inside: { w: 0.44, h: 0.55, d: 0.44, at: [-0.6, 0.62, -0.14] } },
      { part: '독 뚜껑 2', slide: [0, 0.22, 0], inside: { w: 0.4, h: 0.5, d: 0.4, at: [-0.02, 0.58, 0.16] } },
      { part: '독 뚜껑 3', slide: [0, 0.2, 0], inside: { w: 0.32, h: 0.38, d: 0.32, at: [0.5, 0.5, -0.16] } },
      { part: '독 뚜껑 4', slide: [0, 0.18, 0], inside: { w: 0.26, h: 0.3, d: 0.26, at: [0.78, 0.46, 0.2] } },
    ],
    build(w, d) {
      // --- 축대 ---
      w.part('축대');
      w.at(0, d.h / 2, 0, () => {
        w.add(bevelBox(d.w, d.h, d.d, 0.012), 'concrete', 0.5);
      });

      /** 독 하나 — 배가 부르고 어깨에서 좁아지는 옹기. r 은 배 반지름 */
      const jar = (r: number, hh: number, n: number) => {
        w.part('독');
        w.add(
          lathe(
            [
              [0.001, 0],
              [r * 0.56, 0.005],
              [r * 0.92, hh * 0.22],
              [r, hh * 0.45],
              [r * 0.86, hh * 0.74],
              [r * 0.66, hh * 0.95],
              [r * 0.70, hh],
              [0.001, hh],
            ],
            16,
          ),
          'earthen',
          0.35,
        );
        // 뚜껑 — 아가리보다 조금 크게 덮인다. 눈은 여기 얹힌다
        w.part('뚜껑');
        w.movable('독 뚜껑 ' + n, () => {
          w.at(0, hh, 0, () => {
            w.add(
              lathe(
                [
                  [0.001, 0], [r * 0.78, 0], [r * 0.74, 0.016],
                  [r * 0.5, 0.055], [r * 0.16, 0.072], [r * 0.13, 0.1], [0.001, 0.1],
                ],
                16,
              ),
              'earthen',
              0.3,
            );
          });
        });
      };

      // 큰 독 둘, 중간 둘. 축대 위에 **정확히** 앉혀야 덩이가 하나로 붙는다
      const spots: [number, number, number, number][] = [
        [-0.6, -0.14, 0.30, 0.72],
        [-0.02, 0.16, 0.27, 0.64],
        [0.5, -0.16, 0.22, 0.5],
        [0.78, 0.2, 0.18, 0.4],
      ];
      for (let i = 0; i < spots.length; i++) {
        const [x, z, r, hh] = spots[i];
        w.at(x, d.h, z, () => jar(r, hh, i + 1));
      }
    },
    materials: (r) => snowedSlots(r, ['concrete', 'earthen']),
  },

  /* ================================================================ */
  {
    id: 'wood_pile',
    nofront: true,
    name: '장작더미',
    collection: 'sahol',
    category: '마을',
    note:
      '처마 밑에 벽을 따라 쌓아 둔 장작. **자른 단면이 앞(-Z)을 본다** — 옆으로 눕혀 ' +
      '쌓으면 통나무 담이 되어 버린다. 굵기가 제각각이고 켜켜이 어긋나 있어야 하며, ' +
      '맨 윗줄만 눈을 이고 있다. 아랫줄이 하얘지면 눈이 나무를 뚫고 들어간 꼴이 된다.',
    dims: { w: 1.7, h: 0.92, d: 0.5 },
    /* 맨 위에 가로로 걸친 장작을 집어 든다 — 더미는 그대로다 (2026-09-16) */
    openable: [{ part: '장작', slide: [0, 0, -0.18] }],
    build(w, d) {
      /**
       * **줄을 격자로 짜면 안 된다.**
       *
       * 처음에는 일정한 피치로 원기둥을 깔고 한 줄 걸러 반 칸씩 밀었다. 벌집이
       * 나왔다 — 굵기가 똑같은 통나무가 육각으로 딱 맞물린 벽이라 사람이 쌓은
       * 더미가 아니라 파이프를 묶어 놓은 것으로 보였다.
       *
       * 실제 장작더미가 불규칙한 이유는 **팬 장작의 폭이 제각각**이기 때문이다.
       * 그래서 피치를 정하지 않고 왼쪽부터 조각을 하나씩 놓으면서 그 조각의
       * 폭만큼 커서를 옮긴다. 반쪽 낸 것(쐐기)과 통짜(원기둥)를 섞으면 줄마다
       * 개수가 다르게 나오고, 그게 벌집을 깬다.
       *
       * 겹침 6mm 는 joint-check 때문이다 — 딱 붙이면 부동소수 오차로 틈이 생겨
       * 더미가 열 덩이로 쪼개진다.
       */
      const OVER = 0.006;
      let i = 0;
      let y = 0;
      for (let row = 0; y < d.h - 0.06; row++) {
        // 줄 높이는 그 줄에서 제일 굵은 것이 정한다. 여기서는 미리 뽑아 둔다
        const rowR = 0.052 + rnd(row * 13 + 3) * 0.026;
        // 맨 윗줄은 중간에서 끊긴다. 반듯하게 채운 더미는 「다 쓰지 않은 더미」로
        // 안 보인다 — 겨울 내내 빼 쓴 자리가 있어야 한다
        const last = y + rowR * 2 > d.h - 0.06;
        const limit = last ? -d.w / 2 + (0.3 + rnd(row + 77) * 0.45) * d.w : d.w / 2;
        let x = -d.w / 2;
        while (x < limit) {
          const split = rnd(i + 501) > 0.42;
          const r = rowR * (0.82 + rnd(i + 21) * 0.36);
          const bw = split ? r * (1.5 + rnd(i + 61) * 0.7) : r * 2;
          if (x + bw > limit + 0.02) break;
          // 길이도 제각각이라 앞면이 들쭉날쭉해야 한다. 다 같은 길이로 자르면
          // 톱질한 합판 단면처럼 앞이 평평해진다
          const len = d.d * (0.82 + rnd(i + 131) * 0.18);
          const z = (rnd(i + 171) - 0.5) * (d.d - len);
          const cx = x + bw / 2;
          const tilt = (rnd(i + 91) - 0.5) * 0.34;
          // 굵은 놈이 맨 아랫줄에 걸리면 반지름이 줄 높이를 넘어 **바닥을 뚫는다.**
          // 자기 반높이보다 아래로는 못 내려가게 막는다 (조각 원점 y=0 이 접지면이다)
          const halfH = split
            ? (rowR * 1.9 * Math.cos(tilt) + bw * Math.abs(Math.sin(tilt))) / 2
            : r;
          const cy = Math.max(y + rowR + (rnd(i + 41) - 0.5) * 0.008, halfH + 0.001);
          const slot: Slot = rnd(i + 11) > 0.62 ? 'walnut' : 'oak';
          w.part(split ? '팬장작' : '통장작');
          if (split) {
            // 쐐기 — 세로로 세운 판을 조금 기울인다. 각도가 있어야 「던져 쌓은」 티가 난다
            w.at(cx, cy, z, () => {
              w.add(bevelBox(bw, rowR * 1.9, len, 0.004), slot, 0.26);
            }, 0, 0, tilt);
          } else {
            w.at(cx, cy, z, () => {
              w.add(new THREE.CylinderGeometry(r, r * 0.93, len, 9), slot, 0.28);
            }, 0, Math.PI / 2);
          }
          x += bw - OVER;
          i++;
        }
        y += rowR * 1.86;
      }
      // 맨 위에 가로로 걸친 것 둘 — 더미 꼭대기는 늘 이렇게 마무리된다.
      // 이게 있으면 「쌓다 만 벽」이 아니라 「덮어 둔 더미」로 읽힌다
      for (let k = 0; k < 2; k++) {
        const cx = -d.w / 2 + 0.2 + rnd(k + 900) * (d.w * 0.45);
        w.part('가로걸이');
        const drawLog = () => {
          w.at(cx, y - 0.02, (rnd(k + 910) - 0.5) * 0.1, () => {
            w.add(new THREE.CylinderGeometry(0.055, 0.05, d.w * 0.33, 9), 'walnut', 0.28);
          }, 0, 0, Math.PI / 2 + (rnd(k + 920) - 0.5) * 0.14);
        };
        // 맨 위 한 개만 집을 수 있게 뗀다
        if (k === 0) w.movable('장작', drawLog); else drawLog();
      }
    },
    materials: (r) => snowedSlots(r, ['oak', 'walnut']),
  },

  /* ================================================================ */
  {
    id: 'hand_cart',
    nofront: true,
    name: '리어카',
    collection: 'sahol',
    category: '마을',
    note:
      '마당이나 길가에 세워 둔 손수레. 손잡이가 앞(-Z)으로 나오고, 그 앞 끝을 받침다리 둘이 ' +
      '땅에서 받쳐 수평으로 선다 — 받침다리가 없으면 누가 자루를 잡고 있는 것처럼 보인다. ' +
      '짐칸 바닥에 눈이 고여 있어야 「어제부터 세워 둔 것」이 된다. 바퀴는 지름 0.62m 고무 타이어.',
    dims: { w: 1.02, L: 1.35, wheel: 0.62, bed: 0.52 },
    /* 손잡이를 잡고 끌어 본다 — 수레째 앞(−Z)으로 끌려 나온다 (2026-09-16) */
    openable: [{ part: '리어카', slide: [0, 0, -0.18] }],
    build(w, d) {
      w.movable('리어카', () => {
      const wr = d.wheel / 2;
      const bedY = d.bed;

      // --- 짐칸 ---
      // 바닥 합판 + 낮은 옆판 셋. 뒤(+Z)는 트여 있다
      w.part('짐칸바닥');
      w.at(0, bedY, 0, () => {
        w.add(bevelBox(d.w, 0.028, d.L, 0.004), 'oak', 0.5);
      });
      // 옆판 0.32 — 옆에서 봤을 때 짐칸이 「밥상 테두리」가 아니라 「담는 통」으로 읽히는 높이
      const sideH = 0.32;
      w.part('옆판');
      w.mirrorX(d.w / 2 - 0.012, bedY + sideH / 2, 0, () => {
        w.add(bevelBox(0.024, sideH, d.L, 0.004), 'steel', 0.3);
      });
      w.part('앞판');
      w.at(0, bedY + sideH / 2, -d.L / 2 + 0.012, () => {
        w.add(bevelBox(d.w, sideH, 0.024, 0.004), 'steel', 0.3);
      });
      // 바닥 가로 리브 셋 — 짐칸이 판 한 장이면 밥상으로 보인다. 리어카 바닥은
      // 각재가 지나가고 그 위에 합판이 얹힌 것이라 밑에서 보면 줄이 있다
      for (const rz of [-d.L / 3, 0, d.L / 3]) {
        w.part('바닥리브');
        w.at(0, bedY - 0.024, rz, () => {
          w.add(bevelBox(d.w - 0.04, 0.03, 0.05, 0.003), 'steel', 0.25);
        });
      }

      // --- 세로 프레임 · 손잡이 ---
      // 짐칸 밑을 지나 앞으로 뻗어 손잡이가 된다. 한 부재로 만들어야 리어카가 된다
      const hx = d.w / 2 - 0.09;
      w.part('프레임');
      w.mirrorX(hx, bedY - 0.03, -0.18, () => {
        w.add(bevelBox(0.04, 0.04, d.L + 0.62, 0.004), 'steel', 0.3);
      });
      // 그립은 **자루 방향(Z)** 으로 눕는다. 세로로 세우면 손잡이가 아니라
      // 프레임에 꽂힌 말뚝으로 보인다
      w.part('손잡이');
      w.mirrorX(hx, bedY - 0.03, -d.L / 2 - 0.42, () => {
        w.at(0, 0, 0, () => cyl(w, 'rubber', 0.026, 0.26, 8, 0.2), 0, Math.PI / 2);
      });
      // 가로 부재 둘 — 좌우 프레임을 잇는다
      for (const cz of [-d.L / 2 + 0.16, d.L / 2 - 0.16]) {
        w.part('가로재');
        w.at(0, bedY - 0.03, cz, () => {
          w.add(bevelBox(d.w - 0.14, 0.035, 0.035, 0.003), 'steel', 0.3);
        });
      }

      // --- 바퀴 ---
      // 축은 X. 타이어는 온전한 토러스라 닫혀 있다
      w.part('바퀴');
      w.mirrorX(d.w / 2 + 0.055, wr, 0.06, (s) => {
        w.at(0, 0, 0, () => {
          /* **타이어와 휠이 서로 직각이었다**(2026-09-09 Chris: 「바퀴 똑바로」).
           * 감싼 `w.at(..., rotZ=π/2)` 는 원기둥 축을 Y→X 로 돌려 휠·허브는 맞는데,
           * 토러스는 축이 **Z** 라 같은 회전으로는 제자리에서 돌 뿐이다.
           * 먼저 rotateX(π/2) 로 축을 Z→−Y 로 눕히면, 바깥 rotZ 가 −Y→+X 로 돌려
           * 휠과 축이 맞는다. */
          const tire = new THREE.TorusGeometry(wr - 0.045, 0.045, 8, 18);
          tire.rotateX(Math.PI / 2);
          w.add(tire, 'rubber', 0.25);
          w.add(new THREE.CylinderGeometry(wr - 0.075, wr - 0.075, 0.03, 16), 'steel', 0.3);
          w.add(new THREE.CylinderGeometry(0.045, 0.045, 0.09, 10), 'steel', 0.2);
        }, 0, 0, Math.PI / 2);
        void s;
      });
      // 차축 — 두 바퀴와 프레임을 잇는다. 없으면 바퀴가 따로 노는 덩이가 된다
      w.part('차축');
      w.at(0, wr, 0.06, () => {
        cyl(w, 'steel', 0.022, d.w + 0.14, 8, 0.2);
      }, 0, 0, Math.PI / 2);
      w.part('바퀴받이');
      // 차축(wr)에서 짐칸 밑(bedY−0.03)까지. 중심에 wr/2 를 한 번 더 더해 짐칸 위로 13.5cm 솟아
      // 있었다(CI audit top_pierce 2026-09-09) — 중심은 두 끝의 평균이다
      w.mirrorX(hx, (bedY - 0.03 + wr) / 2, 0.06, () => {
        w.add(bevelBox(0.03, bedY - 0.03 - wr + 0.04, 0.03, 0.003), 'steel', 0.25);
      });

      // --- 받침다리 ---
      // 앞쪽 프레임에서 땅까지. 이게 있어야 세워 둔 물건이 된다
      w.part('받침다리');
      w.mirrorX(hx, (bedY - 0.03) / 2, -d.L / 2 + 0.02, () => {
        w.add(bevelBox(0.028, bedY - 0.03, 0.028, 0.003), 'steel', 0.25);
      });
      });
    },
    materials: (r) => snowedSlots(r, ['steel', 'oak', 'rubber']),
  },
  /* ================================================================ */
  {
    id: 'dig_ground',
    nofront: true,
    name: '눈밭',
    collection: 'sahol',
    category: '마을',
    note:
      '**땅과 똑같은 파기 자리 — 아무것도 그리지 않는다.** 5cm 껍질도 얼룩으로 ' +
      '읽혀 「너무 티나」 반려됐다 (2026-09-18, Chris: 「열기 전 후로 하지말고 ' +
      '다른 땅이랑 똑같이」). 기하학은 buildLid 의 히트박스 크기를 재는 ' +
      '자루일 뿐, 재질이 invisible 이라 닫혀도 열려도 눈밭 그대로다. ' +
      '삽(shovel) 없이는 프롬프트도 안 뜬다.',
    dims: { w: 0.9, h: 0.2, d: 0.9 },
    build(w, d) {
      /* hitBox 는 조각의 fit 크기에서 나온다 — 조준 영역을 0.9×0.9 로 맞추는 박스 */
      w.add(new THREE.BoxGeometry(d.w, d.h, d.d), 'porcelain', 0.6);
    },
    /* 어떤 재질이든 visible:false — 화면에는 땅만 보인다 */
    materials: () => ({ porcelain: new THREE.MeshBasicMaterial({ visible: false }) }),
  },
  /* ================================================================ */
  {
    id: 'dig_ground_packed',
    variantOf: 'dig_ground',
    nofront: true,
    name: '다져진 눈밭',
    collection: 'sahol',
    category: '마을',
    note:
      '**다져진 눈밭 — L-07 가로등 기둥 밑의 파기 자리** (사흘 v8 1-c — 「기둥 밑 눈이 ' +
      '주변보다 낮고 다져짐」). 발자국도 파인 자국도 아닌, 살짝 눌려 어두워진 넓은 ' +
      '원반 하나뿐 — 「기둥 밑을 유심히 본 사람」에게만 읽힌다. PropDef 에 dims 가 ' +
      '없어서 dig_ground 와 조각을 나눴다.',
    dims: { w: 0.9, h: 0.2, d: 0.9 },
    build(w, d) {
      const disc = new THREE.CylinderGeometry(0.42, 0.46, 0.012, 28);
      disc.translate(0, 0.006, 0);
      w.add(disc, 'frost', 0.5);
      w.add(new THREE.BoxGeometry(d.w, d.h, d.d), 'porcelain', 0.6);
    },
    materials: () => ({
      porcelain: new THREE.MeshBasicMaterial({ visible: false }),
      /* 이 조각 안에서만 frost 를 다져진 눈으로 덮어쓴다 */
      frost: new THREE.MeshStandardMaterial({ color: '#c6cfd8', roughness: 0.62, metalness: 0.02 }),
    }),
  },
  /* ================================================================ */
  {
    id: 'capsule_marker',
    /* 표석은 눈·흙에 살짝 파묻혀 선다 — 원점 아래 2cm 는 의도 */
    sunk: true,
    name: '표석과 팻말',
    collection: 'sahol',
    category: '마을',
    note:
      '**교문 옷 타임캡슐 자리 — 납작한 표석 하나와 나무 팻말 하나가 함께 선 조각** ' +
      '(사흘 v8 1-c — 「교문 옆 표석 아래, 730일 후에」). 판면의 글(「730일 후에」)은 ' +
      '단서 본문이 말하고 조각은 형태만 만든다. 표석 윗면에는 눈이 얹힌다.',
    dims: { sw: 0.52, sh: 0.34, sd: 0.3, pw: 0.42, ph: 0.24, t: 0.024 },
    build(w, d) {
      // --- 표석 — 다듬지 않은 납작한 돌, 살짝 비스듬히
      w.part('표석');
      w.at(0.3, d.sh / 2 - 0.02, 0.12, () => {
        const g = bevelBox(d.sw, d.sh, d.sd, 0.05);
        g.rotateZ(0.03);
        g.rotateY(0.12);
        w.add(g, 'concrete', 0.5);
      });
      // --- 팻말 — 말뚝 하나에 판 하나, 살짝 앞으로 기울임
      w.part('말뚝');
      w.at(-0.2, 0.4, -0.08, () => {
        w.add(bevelBox(0.045, 0.8, 0.045, 0.004), 'oak', 0.3);
      });
      w.part('판');
      w.at(-0.2, 0.72, -0.094, () => {
        const g = bevelBox(d.pw, d.ph, d.t, 0.006);
        g.rotateX(-0.14);
        w.add(g, 'oak', 0.35);
      });
    },
    materials: (r) => snowedSlots(r, ['concrete', 'oak']),
  },
  /* ================================================================ */
  {
    id: 'stake_sign',
    nofront: true,
    name: '팻말',
    collection: 'sahol',
    category: '마을',
    note:
      '**눈밭에 꽂힌 작은 나무 팻말.** 교문 옆 타임캡슐 자리 표시 — 밭두렁·공사 ' +
      '자리에 세우는 사립 팻말처럼 말뚝 하나에 판 하나다. 판면의 글(「730일 후에」)은 ' +
      '단서 본문이 말하고 조각은 형태만 만든다. 판은 살짝 앞(−Z)으로 기울여 둔다 — ' +
      '다가서는 사람이 내려다보는 각이고, 윗면에 눈이 얹힌다.',
    dims: { pw: 0.42, ph: 0.24, t: 0.024 },
    build(w, d) {
      // --- 말뚝 ---
      w.part('말뚝');
      w.at(0, 0.4, 0, () => {
        w.add(bevelBox(0.045, 0.8, 0.045, 0.004), 'oak', 0.3);
      });
      // --- 판 --- 말뚝 앞면에 붙어 살짝 위를 본다
      w.part('판');
      w.at(0, 0.72, -0.014, () => {
        const g = bevelBox(d.pw, d.ph, d.t, 0.006);
        g.rotateX(-0.14);
        w.add(g, 'oak', 0.35);
      });
    },
    materials: (r) => snowedSlots(r, ['oak']),
  },
];
